/* ONYX — commercial package builder
   Minimal vanilla JavaScript. No framework, no libraries, no dependencies.

   PHASE ONE SCOPE — read this before changing the pricing logic:
   Shopify cannot apply a JavaScript-calculated discount at checkout. This file
   therefore computes an INDICATIVE package figure for display and writes the
   same figure into the quote email. It never claims the discount is applied and
   never alters a price.

   SUBMISSION: the page uses a real Shopify contact form. This script does NOT
   submit it — the browser does, natively. All the script does is keep
   contact[body] populated with the current package, and reveal the form once
   something is selected. Nothing is added to the basket: there are no basket
   routes, no order note and no fetch requests in this file at all.

   No tier percentage or minimum quantity is hard-coded here. Both arrive from
   section settings via the data-tiers attribute.

   Progressive enhancement: without this file the page still renders the hero,
   every machine row with a real product link and price, and a working contact
   form — a customer can still send an enquiry, just without the composed
   package summary. */
(function () {
  'use strict';

  /* ---------- Money ----------
     Formats from the shop's own money_format so currency presentation matches
     the rest of the store. */
  function makeMoney(format, currency) {
    return function (cents) {
      var amount = (cents / 100).toFixed(2);
      var parts = amount.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var withCents = whole + '.' + parts[1];

      if (format && format.indexOf('{{') !== -1) {
        return format
          .replace(/\{\{\s*amount\s*\}\}/g, withCents)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCents)
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole);
      }
      return '$' + withCents + (currency ? ' ' + currency : '');
    };
  }

  function Builder(root) {
    this.root = root;
    this.money = makeMoney(root.getAttribute('data-money-format'), root.getAttribute('data-currency'));

    this.discountsOn = root.getAttribute('data-discounts') === '1';
    this.persist = root.getAttribute('data-persist') === '1';
    this.storageKey = root.getAttribute('data-storage-key') || 'onyx-pkb';

    /* Tiers sorted ascending by minimum, so "current" is the last one met and
       "next" is the first one not met. A tier with pct 0 stays in the list as a
       threshold but is never allowed to produce a saving. */
    this.tiers = [];
    try {
      var raw = JSON.parse(root.getAttribute('data-tiers') || '[]');
      this.tiers = raw
        .filter(function (t) { return t && t.min > 0; })
        .sort(function (a, b) { return a.min - b.min; });
    } catch (e) {
      this.tiers = [];
    }

    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-onyx-card]'));
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-onyx-tab]'));

    this.el = {
      count: root.querySelector('[data-onyx-count]'),
      subtotal: root.querySelector('[data-onyx-subtotal]'),
      savingRow: root.querySelector('[data-onyx-saving-row]'),
      saving: root.querySelector('[data-onyx-saving]'),
      totalRow: root.querySelector('[data-onyx-total-row]'),
      total: root.querySelector('[data-onyx-total]'),
      progress: root.querySelector('[data-onyx-progress]'),
      tierLabel: root.querySelector('[data-onyx-tier-label]'),
      progressMsg: root.querySelector('[data-onyx-progress-msg]'),
      form: root.querySelector('[data-onyx-form]'),
      actions: root.querySelector('[data-onyx-actions]'),
      body: root.querySelector('[data-onyx-body]'),
      suburb: root.querySelector('[data-onyx-suburb]'),
      notes: root.querySelector('[data-onyx-notes]'),
      submit: root.querySelector('[data-onyx-submit]'),
      clear: root.querySelector('[data-onyx-clear]'),
      status: root.querySelector('[data-onyx-status]'),
      posted: root.querySelector('[data-onyx-posted]'),
      none: root.querySelector('[data-onyx-none]'),
      bar: root.querySelector('[data-onyx-bar]'),
      barCount: root.querySelector('[data-onyx-bar-count]'),
      barTotal: root.querySelector('[data-onyx-bar-total]'),
      barJump: root.querySelector('[data-onyx-bar-jump]'),
      panel: root.querySelector('[data-onyx-panel]')
    };

    this.promptCopy = this.el.status ? this.el.status.textContent : '';

    /* A confirmed Shopify success state is the ONLY thing that clears the saved
       package. Shopify renders it on reload after a successful post, so by the
       time this runs the submission is genuinely complete. */
    if (this.el.posted) {
      this.forget();
      this.el.posted.focus({ preventScroll: true });
      return;
    }

    this.bind();
    this.restore();
    this.update();
  }

  /* ---------- Events ---------- */

  Builder.prototype.bind = function () {
    var self = this;

    this.cards.forEach(function (card) {
      var input = card.querySelector('[data-onyx-qty]');
      if (!input) return;

      card.querySelectorAll('[data-onyx-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var delta = parseInt(btn.getAttribute('data-onyx-step'), 10) || 0;
          self.setQty(card, (parseInt(input.value, 10) || 0) + delta);
        });
      });

      input.addEventListener('input', function () { self.setQty(card, parseInt(input.value, 10)); });
      input.addEventListener('change', function () { self.setQty(card, parseInt(input.value, 10)); });
    });

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        self.filter(tab.getAttribute('data-onyx-tab') || '');
      });
    });

    /* Suburb and notes are posted under their own contact[…] names too, but they
       also belong in the body, so the body is recomposed when either changes. */
    [this.el.suburb, this.el.notes].forEach(function (field) {
      if (field) field.addEventListener('input', function () { self.writeBody(); });
    });

    if (this.el.clear) {
      this.el.clear.addEventListener('click', function () { self.clear(); });
    }

    /* Recompose one last time on submit. The body is already current on every
       change; this is belt and braces against an autofilled field that fired no
       input event. The form then submits natively — nothing is intercepted and
       nothing is prevented. */
    var form = this.root.querySelector('form');
    if (form) {
      form.addEventListener('submit', function () { self.writeBody(); });
    }

    if (this.el.barJump && this.el.panel) {
      this.el.barJump.addEventListener('click', function () {
        /* Offset computed and passed to window.scrollTo — the element-scroll
           API is avoided because it can shift the whole theme layout. */
        var top = self.el.panel.getBoundingClientRect().top + window.pageYOffset - 20;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    }
  };

  /* ---------- Quantity ---------- */

  Builder.prototype.setQty = function (card, value) {
    var input = card.querySelector('[data-onyx-qty]');
    if (!input) return;

    var qty = value;
    if (isNaN(qty) || qty < 0) qty = 0;
    if (qty > 99) qty = 99;

    if (String(qty) !== input.value) input.value = String(qty);

    if (qty > 0) {
      card.setAttribute('data-selected', '');
    } else {
      card.removeAttribute('data-selected');
    }

    var minus = card.querySelector('[data-onyx-step="-1"]');
    if (minus) minus.disabled = qty === 0;

    this.update();
    this.save();
  };

  /* ---------- Filter tabs ---------- */

  Builder.prototype.filter = function (value) {
    var shown = 0;

    this.cards.forEach(function (card) {
      /* Whole space-delimited tokens, so 'core' can never match 'core-plus' and
         a machine tagged with several movements appears under each of them. */
      var tokens = ' ' + (card.getAttribute('data-movement') || '') + ' ';
      var match = value === '' || tokens.indexOf(' ' + value + ' ') !== -1;
      card.hidden = !match;
      if (match) shown++;
    });

    this.tabs.forEach(function (tab) {
      tab.setAttribute('aria-pressed', (tab.getAttribute('data-onyx-tab') || '') === value ? 'true' : 'false');
    });

    if (this.el.none) this.el.none.hidden = shown !== 0;
  };

  /* ---------- Totals ---------- */

  Builder.prototype.readState = function () {
    var items = [];
    var count = 0;
    var subtotal = 0;

    this.cards.forEach(function (card) {
      var input = card.querySelector('[data-onyx-qty]');
      var qty = input ? parseInt(input.value, 10) || 0 : 0;
      if (qty <= 0) return;

      var price = parseInt(card.getAttribute('data-price'), 10) || 0;
      count += qty;
      subtotal += price * qty;
      items.push({
        qty: qty,
        price: price,
        title: card.getAttribute('data-title') || '',
        sku: card.getAttribute('data-sku') || '',
        status: card.getAttribute('data-status') || ''
      });
    });

    return { items: items, count: count, subtotal: subtotal };
  };

  /* Current tier = the highest one whose minimum is met. Next tier = the first
     one not met. A zero-percentage tier can be current (it is a real threshold)
     but contributes no saving. */
  Builder.prototype.tierFor = function (count) {
    var current = null;
    var next = null;

    for (var i = 0; i < this.tiers.length; i++) {
      if (count >= this.tiers[i].min) {
        current = this.tiers[i];
      } else {
        next = this.tiers[i];
        break;
      }
    }
    return { current: current, next: next };
  };

  Builder.prototype.update = function () {
    var state = this.readState();
    var money = this.money;

    if (this.el.count) this.el.count.textContent = String(state.count);
    if (this.el.subtotal) {
      this.el.subtotal.textContent = state.count ? money(state.subtotal) : '\u2014';
    }

    var saving = 0;
    var tiers = { current: null, next: null };

    if (this.discountsOn && state.count > 0) {
      tiers = this.tierFor(state.count);
      var pct = tiers.current ? (parseFloat(tiers.current.pct) || 0) : 0;
      if (pct > 0) saving = Math.round(state.subtotal * (pct / 100));
    }

    /* Saving and package total appear ONLY when there is a real saving, so the
       page can never show "$0 saved" or a package total identical to the
       subtotal. With every percentage at zero, neither row ever appears. */
    if (this.el.savingRow) this.el.savingRow.hidden = saving <= 0;
    if (this.el.saving && saving > 0) this.el.saving.textContent = '\u2212' + money(saving);
    if (this.el.totalRow) this.el.totalRow.hidden = saving <= 0;
    if (this.el.total && saving > 0) this.el.total.textContent = money(state.subtotal - saving);

    this.renderProgress(state, tiers);

    this.state = state;
    this.saving = saving;
    this.currentTier = tiers.current;

    /* The form stays out of the way until there is something to quote. */
    var has = state.count > 0;
    if (this.el.form) this.el.form.hidden = !has;
    if (this.el.actions) this.el.actions.hidden = !has;
    if (this.el.status) this.el.status.textContent = has ? '' : this.promptCopy;

    if (this.el.bar) {
      this.el.bar.hidden = !has;
      if (has) {
        this.root.setAttribute('data-bar-visible', '');
      } else {
        this.root.removeAttribute('data-bar-visible');
      }
      if (this.el.barCount) {
        this.el.barCount.textContent = state.count + (state.count === 1 ? ' machine' : ' machines');
      }
      if (this.el.barTotal) {
        this.el.barTotal.textContent = money(saving > 0 ? state.subtotal - saving : state.subtotal);
      }
    }

    this.writeBody();
  };

  Builder.prototype.renderProgress = function (state, tiers) {
    if (!this.el.progress) return;

    if (!this.discountsOn || state.count === 0 || this.tiers.length === 0) {
      this.el.progress.hidden = true;
      return;
    }

    var current = tiers.current;
    var next = tiers.next;
    var nextHasValue = next && (parseFloat(next.pct) || 0) > 0;
    var currentHasValue = current && (parseFloat(current.pct) || 0) > 0;

    /* Nothing to say when no tier with a real percentage is reached and none is
       within reach — which is exactly the all-zero case. */
    if (!currentHasValue && !nextHasValue) {
      this.el.progress.hidden = true;
      return;
    }

    this.el.progress.hidden = false;

    if (this.el.tierLabel) {
      this.el.tierLabel.textContent = currentHasValue && current.label
        ? current.label
        : (nextHasValue ? 'NEXT PACKAGE LEVEL' : '');
    }

    if (this.el.progressMsg) {
      if (nextHasValue) {
        var need = next.min - state.count;
        this.el.progressMsg.textContent =
          'Add ' + need + (need === 1 ? ' more machine' : ' more machines') +
          ' to reach ' + (next.label || (next.pct + '%')) + '.';
      } else if (currentHasValue) {
        this.el.progressMsg.textContent = 'Highest package level reached.';
      }
    }
  };

  /* ---------- contact[body] ----------
     The whole package, in plain text, kept current as the customer builds. This
     is what lands in the quote email. */

  Builder.prototype.writeBody = function () {
    if (!this.el.body) return;

    var state = this.state || this.readState();
    var money = this.money;
    var lines = [];

    if (state.count === 0) {
      this.el.body.value = '';
      return;
    }

    lines.push('SELECTED MACHINES');
    lines.push('');
    state.items.forEach(function (item) {
      var row = item.qty + ' x ' + item.title;
      if (item.sku) row += '  [SKU ' + item.sku + ']';
      if (item.status) row += '  (' + item.status + ')';
      row += '  ' + money(item.price) + ' each';
      lines.push(row);
    });

    lines.push('');
    lines.push('Total machines: ' + state.count);
    lines.push('Equipment subtotal: ' + money(state.subtotal));

    if (this.saving > 0 && this.currentTier) {
      lines.push('Package tier: ' + (this.currentTier.label || ''));
      lines.push('Indicative discount: ' + this.currentTier.pct + '%');
      lines.push('Indicative saving: ' + money(this.saving));
      lines.push('Indicative package total: ' + money(state.subtotal - this.saving));
      lines.push('');
      lines.push('(Package figures are indicative and require confirmation.)');
    }

    var suburb = this.el.suburb ? this.el.suburb.value.trim() : '';
    var notes = this.el.notes ? this.el.notes.value.trim() : '';

    lines.push('');
    lines.push('Delivery suburb or postcode: ' + (suburb || 'Not supplied'));
    lines.push('');
    lines.push('PROJECT NOTES');
    lines.push(notes || 'None supplied');

    lines.push('');
    lines.push('Equipment pricing only. Freight, installation and final availability require confirmation by the ONYX commercial team.');

    this.el.body.value = lines.join('\n');
  };

  /* ---------- Persistence ----------
     Writes ONLY this builder's own key, and only quantities. Never touches any
     other storage entry. */

  Builder.prototype.save = function () {
    if (!this.persist) return;
    try {
      var map = {};
      this.cards.forEach(function (card) {
        var input = card.querySelector('[data-onyx-qty]');
        var qty = input ? parseInt(input.value, 10) || 0 : 0;
        if (qty > 0) map[card.getAttribute('data-variant')] = qty;
      });
      if (Object.keys(map).length === 0) {
        window.localStorage.removeItem(this.storageKey);
      } else {
        window.localStorage.setItem(this.storageKey, JSON.stringify(map));
      }
    } catch (e) { /* storage unavailable — the builder still works for this visit */ }
  };

  Builder.prototype.restore = function () {
    if (!this.persist) return;
    var map;
    try {
      map = JSON.parse(window.localStorage.getItem(this.storageKey) || '{}');
    } catch (e) { return; }
    if (!map) return;

    var self = this;
    this.cards.forEach(function (card) {
      var qty = parseInt(map[card.getAttribute('data-variant')], 10) || 0;
      if (qty > 0) self.setQty(card, qty);
    });
  };

  Builder.prototype.forget = function () {
    try { window.localStorage.removeItem(this.storageKey); } catch (e) {}
  };

  Builder.prototype.clear = function () {
    var self = this;
    this.cards.forEach(function (card) { self.setQty(card, 0); });
    this.forget();
  };

  /* ---------- Boot ---------- */

  function init() {
    document.querySelectorAll('[data-onyx-pkb]').forEach(function (root) {
      if (root.__onyxPkb) return;
      root.__onyxPkb = new Builder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
